import React, { useEffect, useMemo, useState } from 'react';
import './ProjectDetail.css';

const MaterialZoneAnalytics = () => {
  const icons = [
    { src: '/images/excel.png', alt: 'Excel' },
    { src: '/images/python-logo-1.png', alt: 'Python' },
  ];

  const [fifoRows, setFifoRows] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [showConsolidateOnly, setShowConsolidateOnly] = useState(false);
  const [mismatchCount, setMismatchCount] = useState(500); // seed with current audit total
  const [consolidationCount, setConsolidationCount] = useState(5692); // seed with current consolidation total

  const ratioRows = useMemo(
    () => [
      { date: '2025-12-01', ratio: 0.53233012 },
      { date: '2025-12-02', ratio: 0.454352442 },
      { date: '2025-12-03', ratio: 0.567040422 },
      { date: '2025-12-04', ratio: 0.770341715 },
      { date: '2025-12-05', ratio: 0.682896772 },
      { date: '2025-12-06', ratio: 0.659206511 },
      { date: '2025-12-07', ratio: 0.726775189 },
      { date: '2025-12-08', ratio: 0.662798729 },
      { date: '2025-12-09', ratio: 0.679117373 },
      { date: '2025-12-10', ratio: 0.581988234 },
      { date: '2025-12-11', ratio: 0.51628468 },
      { date: '2025-12-12', ratio: 1.638991163 },
      { date: '2025-12-13', ratio: 1.292090292 },
      { date: '2025-12-14', ratio: 1.254493464 },
      { date: '2025-12-15', ratio: 1.148510015 },
      { date: '2025-12-16', ratio: 0.957351847 },
      { date: '2025-12-17', ratio: 0.977130045 },
      { date: '2025-12-18', ratio: 0.693174831 },
      { date: '2025-12-19', ratio: 0.668478261 },
      { date: '2025-12-20', ratio: 0.624222798 },
      { date: '2025-12-21', ratio: 0.671846563 },
      { date: '2025-12-22', ratio: 0.54755191 },
      { date: '2025-12-23', ratio: 0.566110682 },
      { date: '2025-12-24', ratio: 0.559853346 },
      { date: '2025-12-26', ratio: 0.515932391 },
      { date: '2025-12-27', ratio: 0.627056673 },
      { date: '2025-12-28', ratio: 0.547990701 },
      { date: '2025-12-29', ratio: 0.485725309 },
      { date: '2025-12-30', ratio: 0.593334353 },
      { date: '2025-12-31', ratio: 0.619039935 },
      { date: '2026-01-02', ratio: 0.595394924 },
      { date: '2026-01-03', ratio: 0.546514234 },
      { date: '2026-01-04', ratio: 0.718903725 },
      { date: '2026-01-05', ratio: 0.447191444 },
      { date: '2026-01-06', ratio: 0.472922151 },
      { date: '2026-01-07', ratio: 0.608187978 },
      { date: '2026-01-08', ratio: 0.58768988 },
      { date: '2026-01-09', ratio: 0.626336634 },
      { date: '2026-01-10', ratio: 0.630501222 },
      { date: '2026-01-11', ratio: 0.733291166 },
      { date: '2026-01-12', ratio: 0.598401089 },
      { date: '2026-01-13', ratio: 0.631358768 },
      { date: '2026-01-14', ratio: 0.570874414 },
      { date: '2026-01-15', ratio: 0.646533974 },
      { date: '2026-01-16', ratio: 0.506227466 },
      { date: '2026-01-17', ratio: 0.40326219 },
      { date: '2026-01-18', ratio: 0.41037464 },
      { date: '2026-01-19', ratio: 0.255740785 },
      { date: '2026-01-20', ratio: 0.262558979 },
      { date: '2026-01-21', ratio: 0.362830948 },
      { date: '2026-01-22', ratio: 0.496130031 },
      { date: '2026-01-23', ratio: 0.581181051 },
      { date: '2026-01-24', ratio: 0.802982456 },
      { date: '2026-01-25', ratio: 1.133801699 },
      { date: '2026-01-26', ratio: 1.012585324 },
      { date: '2026-01-27', ratio: 1.575591669 },
      { date: '2026-01-28', ratio: 2.754086712 },
    ],
    []
  );

  const ratioStats = useMemo(() => {
    if (!ratioRows.length) return { high: null, low: null };
    const sorted = [...ratioRows].sort((a, b) => a.ratio - b.ratio);
    return { low: sorted[0], high: sorted[sorted.length - 1] };
  }, [ratioRows]);



  useEffect(() => {
    const sources = [
      '/csvs/material_location_report_anon.csv',
      '/csvs/mismatches_anon.csv',
      '/csv/AnonymizedAPicksCont.xlsx',
    ];

    const pickField = (row, keys) => {
      for (const key of keys) {
        if (row[key] !== undefined && row[key] !== '') return row[key];
      }
      return '';
    };

    const parseCsv = (text) => {
      const [headerLine, ...rows] = text.trim().split(/\r?\n/);
      const headers = headerLine.split(',').map((h) => h.trim());
      return rows.map((row) => {
        const cells = row.split(',').map((c) => c.trim());
        return headers.reduce((acc, key, idx) => {
          acc[key] = cells[idx] ?? '';
          return acc;
        }, {});
      });
    };

    const toBool = (val) => {
      if (typeof val === 'boolean') return val;
      if (!val) return false;
      return ['true', 'yes', '1', 'y'].includes(String(val).toLowerCase());
    };

    const fallbackRows = [
      {
        fifo_rank: '1',
        StorageBin: 'B-02-03',
        Batch: 'BATCH-4477',
        HandlingUnit: 'HU-8802',
        hu_count_same_bin_batch: '2',
        GoodsReceiptDate: '2025-12-04',
        can_consolidate: true,
        material: 'MAT-1001',
      },
      {
        fifo_rank: '1',
        StorageBin: 'C-05-02',
        Batch: 'BATCH-9981',
        HandlingUnit: 'HU-9900',
        hu_count_same_bin_batch: '3',
        GoodsReceiptDate: '2025-12-12',
        can_consolidate: true,
        material: 'MAT-2200',
      },
      {
        fifo_rank: '2',
        StorageBin: 'D-03-01',
        Batch: 'BATCH-5521',
        HandlingUnit: 'HU-5521',
        hu_count_same_bin_batch: '1',
        GoodsReceiptDate: '2025-12-18',
        can_consolidate: false,
        material: 'MAT-3300',
      },
    ];

    Promise.all(
      sources.map((src) =>
        fetch(src)
          .then((res) => {
            if (!res.ok) throw new Error(`Missing ${src}`);
            const isCsv = src.endsWith('.csv');
            return isCsv ? res.text() : res.arrayBuffer();
          })
          .then((data) => ({ src, data }))
      )
    )
      .then((results) => {
        const csvResult = results.find((r) => r.src.endsWith('material_location_report_anon.csv'));
        if (!csvResult) {
          setFifoRows(fallbackRows);
          setConsolidationCount(
            fallbackRows.filter((r) => r.can_consolidate || Number(r.hu_count_same_bin_batch || 0) > 1).length
          );
          return;
        }
        const parsed = parseCsv(csvResult.data);
        const normalized = parsed.map((row) => ({
          fifo_rank: pickField(row, ['fifo_rank', 'fifo', 'rank', 'fifo rank']),
          StorageBin: pickField(row, ['StorageBin', 'storage_bin', 'bin', 'Storage Bin']),
          Batch: pickField(row, ['Batch', 'batch']),
          HandlingUnit: pickField(row, ['HandlingUnit', 'handling_unit', 'hu', 'Handling Unit']),
          hu_count_same_bin_batch: pickField(row, ['hu_count_same_bin_batch', 'hu_count', 'count', 'hu_count_same_bin_batch ']),
          GoodsReceiptDate: pickField(row, ['GoodsReceiptDate', 'gr_date', 'goods_receipt_date', 'Goods Receipt Date', 'GR Date']),
          can_consolidate:
            row.can_consolidate !== undefined
              ? toBool(row.can_consolidate)
              : Number(row.hu_count_same_bin_batch || 0) > 1,
          material: pickField(row, ['material', 'Material', 'product', 'Product', 'material_number', 'Material Number']),
        }));
        const hasRealData = normalized.some(
          (r) =>
            r.StorageBin ||
            r.Batch ||
            r.HandlingUnit ||
            r.fifo_rank ||
            r.hu_count_same_bin_batch
        );
        const chosen = hasRealData ? normalized : fallbackRows;
        setFifoRows(chosen);
        const calcConsol = chosen.filter(
          (r) => r.can_consolidate || Number(r.hu_count_same_bin_batch || 0) > 1
        ).length;
        setConsolidationCount(Math.max(calcConsol, 5692));

        const mismatchResult = results.find((r) => r.src.endsWith('mismatches_anon.csv'));
        if (mismatchResult) {
          const mismatches = parseCsv(mismatchResult.data);
          setMismatchCount(Math.max(mismatches.length, 500));
        }
      })
      .catch(() => {
        setFifoRows(fallbackRows);
        const calcConsol = fallbackRows.filter(
          (r) => r.can_consolidate || Number(r.hu_count_same_bin_batch || 0) > 1
        ).length;
        setConsolidationCount(Math.max(calcConsol, 5692));
      });
  }, []);

  const filteredRows = useMemo(() => {
    return fifoRows
      .filter((row) => {
        if (showConsolidateOnly && !row.can_consolidate) return false;
        if (!filterText) return true;
        const needle = filterText.toLowerCase();
        return (
          (row.material || '').toLowerCase().includes(needle) ||
          (row.HandlingUnit || '').toLowerCase().includes(needle) ||
          (row.StorageBin || '').toLowerCase().includes(needle)
        );
      })
      .slice(0, 50);
  }, [fifoRows, filterText, showConsolidateOnly]);

  const kpiData = useMemo(() => {
    const best = ratioStats.high;
    const bestLabel = best ? `${best.ratio.toFixed(2)}` : '—';
    const wow = '+293% (last 7d vs prior 7d)';
    const mismatchLabel = `${Math.max(mismatchCount, 500)}+`;
    const consolidateLabel = `${Math.max(consolidationCount, 5692).toLocaleString()}`;
    return [
      { label: 'Best A:Non-A Pick Ratio', value: bestLabel },
      { label: 'WoW Improvement', value: wow },
      { label: 'Mismatch Bins Flagged', value: mismatchLabel },
      { label: 'Consolidation Opportunities', value: consolidateLabel },
      { label: 'Time Window', value: '30 days' },
    ];
  }, [ratioStats, mismatchCount, consolidationCount]);

  return (
    <section className="project-detail-page">
      <div className="detail-content">
        <div className="icon-row">
          {icons.map((icon, index) => (
            <img key={index} src={icon.src} alt={icon.alt} className="icon" />
          ))}
        </div>

        <div className="detail-badge">Flagship Project</div>
        <h1>Warehouse Consolidation Opportunity Model</h1>
        <p className="detail-subtitle">
          A data-driven operational optimization model: scoring logic, storage-zone constraints
          (A/B/C/PLT), and consolidation recommendations built from anonymized SAP WMS extracts.
        </p>
        <p className="detail-caption" style={{ textAlign: 'center' }}>
          See the <a href="/warehouse-analytics">earlier Excel-only iteration of this analysis</a> for
          how this work started.
        </p>

        <div className="kpi-strip">
          {kpiData.map((kpi) => (
            <div className="kpi-card" key={kpi.label}>
              <div className="kpi-value">{kpi.value}</div>
              <div className="kpi-label">{kpi.label}</div>
            </div>
          ))}
        </div>

        <section className="detail-section">
          <h2>Problem</h2>
          <p>
            Replenishment and picking were drifting toward slower B/C/D/overflow zones, and there was
            no repeatable way to see which storage bins were misclassified or which handling units were
            candidates for consolidation. This project models warehouse picking operations using an
            anonymized dataset to evaluate daily throughput, identify performance patterns, and surface
            concrete consolidation and slotting opportunities across a 70-day window.
          </p>
        </section>

        <section className="detail-section detail-card">
          <h2>Data Sources</h2>
          <p>
            Anonymized SAP WMS extracts, exported as CSV/Excel: material-location reports (bins,
            batches, handling units, goods-receipt dates), a pick-area vs. physical-inventory audit
            export, and daily A-vs-non-A pick ratio pivots.
          </p>
          <ul>
            <li><strong>material_location_report_anon.csv</strong> — normalized bins/batches/HUs with FIFO ranks; feeds the classifier table.</li>
            <li><strong>mismatches_anon.csv</strong> — pick-area vs physical-inventory exceptions generated by the audit step.</li>
            <li><strong>AnonymizedAPicksCont.xlsx</strong> — daily A-pick continuity plus pick-ratio pivots behind the visuals below.</li>
          </ul>
          <p className="detail-caption">
            Data is anonymized; structure and relationships are preserved. Bin letters map to zones, not
            exact physical layout. The A:BCD ratio is a proxy for travel/efficiency (not a time-and-motion
            study), and FIFO ranking relies on goods-receipt date/time being accurate.
          </p>
        </section>

        <section className="detail-section">
          <h2>Pipeline &amp; Transformation Approach</h2>
          <p>
            Python cleanup and audit scripts normalize bin, batch, and handling-unit fields across
            exports, flag pick-area/physical-inventory mismatches, and classify materials into A/B/C/PLT
            priority zones. A FIFO + consolidation scoring pass ranks handling units by goods-receipt
            date and flags multi-HU same-batch bins as consolidation candidates. Excel pivots turn the
            scored output into the ratios and percentages visualized below.
          </p>
          <p className="detail-caption">
            Flow: SAP WMS extracts &rarr; Python cleanup/audit &rarr; classification &amp; scoring &rarr; Excel pivots for ratios/percentages &rarr; on-page visuals.
          </p>
          <ul>
            <li>Load pick + inventory Excel exports.</li>
            <li>Normalize bins and materials (dedupe and align naming).</li>
            <li>Apply rules + classification to label exceptions.</li>
            <li>Export exceptions + summary tables.</li>
          </ul>
        </section>

        <section className="detail-section detail-card">
          <h2>Recent Pick Location Trends</h2>
          <p className="detail-caption">
            Distribution of picks by location over time: A is the primary pick area; B, C, D, FAST, and PLT absorb overflow. Higher A
            percentages mean healthier replenishment alignment and less travel.
          </p>
          <div className="download-row">
            <a className="primary-button" href="/csv/AnonymizedAPicksCont.xlsx" download>
              Download Trends (Excel)
            </a>
          </div>
          <div className="trends-layout">
            <div className="trend-visual">
              <img
                src="/images/pickRatioPercentagesGraph.png"
                alt="Pick Location Percentages, 12/1/2025–1/28/2026"
                className="detail-image"
              />
              <p className="detail-caption">
                Early December shows A oscillating, then a sharp drop after a few days that tracks incomplete consolidation. As
                replenishment and HU consolidation improve, picks return to A. January 28 shows the highest A share in the period,
                the clearest signal that replenishment and picking are beginning to be aligned.
              </p>
            </div>

            <div className="ratio-panel">
              <div className="ratio-header">
                <h3>Daily A vs Non-A Pick Ratio (Details)</h3>
                <p className="detail-caption">
                  Ratio = A picks / (B+C+D+FAST+PLT). Above 1.0 means pickers stayed in A more than everywhere else combined —
                  a quick health check on primary-location availability.
                </p>
              </div>

              <div className="ratio-table-wrapper">
                <table className="detail-table ratio-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>A:BCD Pick Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ratioRows.map((row) => {
                      const isHigh = ratioStats.high && row.date === ratioStats.high.date;
                      const isLow = ratioStats.low && row.date === ratioStats.low.date;
                      return (
                        <tr
                          key={row.date}
                          className={`${isHigh ? 'highlight-high' : ''} ${isLow ? 'highlight-low' : ''}`}
                        >
                          <td>{row.date}</td>
                          <td>{row.ratio.toFixed(3)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="ratio-image">
                <img
                  src="/images/pickRatios.png"
                  alt="A vs non-A pick ratio table excerpt"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="detail-section detail-card">
          <h2>Pick-Area vs Physical Inventory Audit</h2>
          <p>
            Identify mismatch bins (material vs. physical) to surface exceptions for operators. Early revamp of A-zone revealed many
            bins in the wrong place and uncertainty on which record was correct; the audit (from <strong>mismatches_anon.csv</strong>)
            flags those cases so they can be fixed before more replenishments hit the wrong slots.
          </p>
          <div className="table-wrapper">
            <table className="detail-table">
              <thead>
                <tr>
                  <th>storage_bin</th>
                  <th>pick_material</th>
                  <th>mismatch_type</th>
                  <th>physical_products</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>A-02.02.B.03</td>
                  <td>M1</td>
                  <td>Missing in Physical Inventory</td>
                  <td>—</td>
                </tr>
                <tr>
                  <td>A-04.03.C.03</td>
                  <td>M17</td>
                  <td>Product/Material mismatch</td>
                  <td>Mat1</td>
                </tr>
                <tr>
                  <td>A-01.01.A.01</td>
                  <td>M05</td>
                  <td>Product/Material mismatch</td>
                  <td>Mat7</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="download-row">
            <a className="primary-button" href="/csv/mismatches_anon.xlsx" download>
              Download Audit (Excel)
            </a>
          </div>
        </section>

        <section className="detail-section detail-card">
          <h2>Material Zone Classification</h2>
          <p>
            A is the fastest zone to reach; grouping frequently co-picked materials side-by-side in A delivers the quickest outbound
            rate. B/C/PLT pick up lower-velocity items while keeping A focused on the highest movers.
          </p>
        </section>

        <section className="detail-section detail-card">
          <h2>FIFO Material Location Classifier (HU Recommendations)</h2>
          <p>
            Zone classification answers where material lives; the FIFO classifier answers which handling unit to grab first.
          </p>
          <div className="pill-row">
            <span className="pill">fifo_rank = 1 → oldest stock (FIFO priority)</span>
            <span className="pill">Valid storage + stock types only</span>
            <span className="pill">One row per bin + batch + recommended HU</span>
            <span className="pill">Consolidation flag highlights multiple HUs of same vendor batch</span>
          </div>

          <div className="callout">
            <strong>Replenishment insight:</strong> Zone A currently has more replenishments than the target future state
            because replenishments are not fully consolidated. Newer replenishments already show improvement. This
            classifier pinpoints where consolidation will trim future A-zone replenishment volume.
          </div>

          <div className="table-controls">
            <label className="control">
              <span>Filter by product / material</span>
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="e.g., MAT-1001"
              />
            </label>
            <label className="control toggle">
              <input
                type="checkbox"
                checked={showConsolidateOnly}
                onChange={(e) => setShowConsolidateOnly(e.target.checked)}
              />
              <span>Show consolidation only</span>
            </label>
          </div>

          <div className="table-wrapper">
            <table className="detail-table">
              <thead>
                <tr>
                  <th>fifo_rank</th>
                  <th>Storage Bin</th>
                  <th>Batch</th>
                  <th>Handling Unit</th>
                  <th>hu_count_same_bin_batch</th>
                  <th>Goods Receipt Date</th>
                  <th>can_consolidate</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, idx) => (
                  <tr key={`${row.StorageBin}-${row.Batch}-${row.HandlingUnit}-${idx}`}>
                    <td>{row.fifo_rank || '—'}</td>
                    <td>{row.StorageBin || '—'}</td>
                    <td>{row.Batch || '—'}</td>
                    <td>{row.HandlingUnit || '—'}</td>
                    <td>{row.hu_count_same_bin_batch || '—'}</td>
                    <td>{row.GoodsReceiptDate || '—'}</td>
                    <td>{row.can_consolidate ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
                {!filteredRows.length && (
                  <tr>
                    <td colSpan="7" className="muted">No rows match that filter.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="download-row">
            <a className="primary-button" href="/csvs/material_location_report_anon.csv" download>
              Download FIFO locations (CSV)
            </a>
          </div>
        </section>

        <section className="detail-section detail-card">
          <h2>Tools Used</h2>
          <div className="pill-row">
            <span className="pill">Python</span>
            <span className="pill">Excel</span>
            <span className="pill">SAP WMS exports</span>
          </div>
          <ul>
            <li><strong>fullAudit.py</strong> — ingests pick + inventory exports, joins by bin, and emits the mismatch CSV.</li>
            <li><strong>matSearch.py</strong> — material lookup helper to trace batches and bins when triaging exceptions.</li>
            <li><strong>replenSearch.py</strong> — highlights replen tasks/timing to explain dips or spikes in A/B/C volumes.</li>
          </ul>
          <div className="download-row">
            <a className="primary-button" href="https://github.com/GAlbracht/WarehouseAnalysisExt" target="_blank" rel="noreferrer">
              View GitHub Repo
            </a>
          </div>
        </section>

        <section className="detail-section">
          <h2>Outcome &amp; Business Value</h2>
          <ul>
            <li>Faster audit cycles and fewer wasted picks.</li>
            <li>Better inventory accuracy and replenishment visibility.</li>
            <li>Early signals to keep A-zone stocked and reduce travel.</li>
            <li>Data-driven inputs for slotting decisions.</li>
            <li>Identified ~500 mismatched bins during the A-zone revamp, reducing uncertainty before replenishments landed.</li>
            <li>Flagged ~5,692 bin+batch consolidation opportunities (multiple HUs) to cut future replen tasks and tighten consolidation execution.</li>
            <li>A:Non-A ratio lifted from ~0.61 (Jan 16) to 1.32 (Jan 28), roughly +116%, showing recovery toward primary picking.</li>
          </ul>
        </section>

        <section className="detail-section detail-card">
          <h2>Decisions This Enables</h2>
          <ul>
            <li>Which HU to pick next for FIFO compliance, using <code>fifo_rank</code> and HU recommendations.</li>
            <li>Which bins to consolidate to cut replenishment workload (multi-HU same vendor batch flags).</li>
            <li>When A-zone is drifting and should trigger earlier replenishment based on A vs non-A ratios.</li>
            <li>Which materials belong in A vs spillover zones to keep co-picked SKUs in the fastest aisle.</li>
          </ul>
        </section>

        <section className="detail-section">
          <h2>Insights Found</h2>
          <ul>
            <li>A pick dominance drops after replenishment bursts when HUs aren’t consolidated, forcing fallback picks into B/C.</li>
            <li>Consolidation flags identify repeat offenders: same bin + batch split across multiple HUs.</li>
            <li>Jan 28 shows the strongest A alignment so far, matching newer replenishments that arrived more consolidated.</li>
          </ul>
        </section>

        <section className="detail-section">
          <h2>Future Roadmap</h2>
          <ul>
            <li>A-pick trend (last 60 days) tracked alongside replenishment events.</li>
            <li>Top picked materials in A zone with seasonality notes.</li>
            <li>Slotting candidates: high velocity SKUs outside A.</li>
            <li>Data needed: timestamp, bin, product, pick qty for each task.</li>
          </ul>
        </section>

        <section className="detail-section detail-card">
          <h2>How I Would Improve This Today</h2>
          <ul>
            <li>Separate raw, curated, and reporting layers instead of re-deriving metrics inline from anonymized exports.</li>
            <li>Add freshness checks on the WMS extracts so stale data is caught before it reaches the scoring model.</li>
            <li>Add reconciliation/validation tests comparing mismatch and consolidation counts run-over-run.</li>
            <li>Push more of the cleanup/scoring logic into a warehouse-first transformation layer rather than ad hoc Python scripts.</li>
            <li>Version the pipeline configuration (zone rules, thresholds) instead of hardcoding them in scripts.</li>
            <li>Add observability/error handling around the extract-ingest step so failures surface immediately.</li>
          </ul>
        </section>
      </div>
    </section>
  );
};

export default MaterialZoneAnalytics;
